import { url, type SafeURL } from '@figma/safe-url';

/** An API path built from fixed route syntax and individually encoded identifiers. */
export type ApiPath = SafeURL;

type ApiPathValue = string | number | null | undefined;

/** Keep query parameters in the transport's `params` option, separate from route selection. */
export function assertApiPath(path: string): void {
  if (
    !path.startsWith('/') ||
    path.startsWith('//') ||
    /[?#\\\s]/.test(path) ||
    new URL(path, 'https://api.invalid').pathname !== path
  ) {
    throw new TypeError('Expected a relative API path without query parameters or URL normalization');
  }
}

/**
 * Substitutions are identifier data. Do not pre-encode values or interpolate route syntax.
 * The shared URL builder also rejects whole dot segments, which encodeURIComponent leaves intact.
 */
export function apiPath(strings: TemplateStringsArray, ...values: ApiPathValue[]): ApiPath {
  for (const value of values) {
    // The shared URL builder inserts objects such as a SafeURLComponent unencoded, and parsed JSON can hold one.
    if ((typeof value !== 'string' && typeof value !== 'number') || value === '') {
      throw new TypeError('API path identifiers must be present and nonempty strings or numbers');
    }
  }
  const path = url(strings, ...values);
  assertApiPath(path);
  return path;
}

/** The `:name` segments of a route pattern, e.g. `'workspaceId' | 'fontId'`. */
type ApiRouteParamName<P extends string> = P extends `${string}/:${infer Name}/${infer Rest}`
  ? Name | ApiRouteParamName<`/${Rest}`>
  : P extends `${string}/:${infer Name}`
    ? Name
    : never;

/**
 * Identifier data for each `:name` segment of `P`, encoded the same way as an {@link apiPath}
 * substitution. As there, a missing or empty value throws before any request is sent.
 */
export type ApiRouteParams<P extends string> = Record<ApiRouteParamName<P>, ApiPathValue>;

/** A backend route. Call it for a request path, or read `pattern` to match requests in tests and mocks. */
export interface ApiRoute<P extends string = string> {
  (...params: [ApiRouteParamName<P>] extends [never] ? [] : [ApiRouteParams<P>]): ApiPath;
  /** The route with a `:name` placeholder for each identifier, e.g. `/v1/recipes/:recipeId`. */
  readonly pattern: P;
}

const PARAM_SEGMENT = /^:([A-Za-z][A-Za-z0-9]*)$/;

/**
 * Turns a fixed route pattern into an {@link ApiRoute}. Lint limits callers to `src/api/apiRoutes.ts`
 * and string literal patterns, so route syntax never comes from data.
 */
export function defineApiRoute<const P extends string>(pattern: P): ApiRoute<P> {
  assertApiPath(pattern);
  const strings = [''];
  const names: string[] = [];
  for (const segment of pattern.split('/').slice(1)) {
    const name = PARAM_SEGMENT.exec(segment)?.[1];
    if (name) {
      strings[strings.length - 1] += '/';
      names.push(name);
      strings.push('');
    } else if (segment.includes(':')) {
      throw new TypeError(`Route parameters must be whole \`:name\` segments: ${pattern}`);
    } else {
      strings[strings.length - 1] += `/${segment}`;
    }
  }
  const template = Object.assign([...strings], { raw: strings }) as unknown as TemplateStringsArray;
  const build = (params?: Partial<Record<string, ApiPathValue>>) =>
    apiPath(template, ...names.map((name) => params?.[name]));
  return Object.assign(build, { pattern });
}
